import React, { useState, useEffect } from 'react';
import { sliderService } from '../../api/sliderService';
import { uploadService } from '../../api/uploadService';
import '../Common/Common.css';

function SliderForm({ slider, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link_url: '',
    is_active: true,
    position: 0,
  });
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (slider) {
      setFormData({
        title: slider.title || '',
        image_url: slider.image_url || '',
        link_url: slider.link_url || '',
        is_active: slider.is_active ?? true,
        position: slider.position || 0,
      });
      setImagePreview(slider.image_url || '');
    }
  }, [slider]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La taille de l\'image doit être inférieure à 5MB');
      return;
    }

    try {
      setUploadingImage(true);

      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload the image to sliders folder
      const result = await uploadService.uploadMedia(file, 'image', 'sliders');
      setFormData({ ...formData, image_url: result.url });
      setImageFile(null);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Échec du téléchargement de l\'image: ' + (error.response?.data?.error || error.message));
      setImagePreview('');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.image_url) {
      alert('Veuillez télécharger une image');
      return;
    }

    setLoading(true);
    try {
      if (slider) {
        await sliderService.updateSlider(slider.id, formData);
      } else {
        await sliderService.createSlider(formData);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving slider:', error);
      alert('Échec de l\'enregistrement du slider');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <div className="form-group">
        <label htmlFor="title">Titre (Optionnel)</label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="form-control"
          placeholder="Titre du slider"
        />
      </div>

      <div className="form-group">
        <label htmlFor="image">Image *</label>
        <div style={{ marginBottom: '10px' }}>
          <input
            type="file"
            id="image"
            accept="image/*"
            onChange={handleImageSelect}
            className="form-control"
            required={!formData.image_url}
            disabled={uploadingImage}
          />
          {uploadingImage && (
            <div style={{ marginTop: '10px', color: '#3182ce', fontSize: '14px' }}>
              Téléchargement de l'image...
            </div>
          )}
        </div>
        {imagePreview && (
          <div style={{ marginTop: '10px' }}>
            <img
              src={imagePreview}
              alt="Aperçu"
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="link_url">URL du lien (Optionnel)</label>
        <input
          type="url"
          id="link_url"
          name="link_url"
          value={formData.link_url}
          onChange={handleChange}
          className="form-control"
          placeholder="https://example.com/product"
        />
        <small>Où les utilisateurs vont lorsqu'ils cliquent sur le slider</small>
      </div>

      <div className="form-group">
        <label htmlFor="position">Position</label>
        <input
          type="number"
          id="position"
          name="position"
          value={formData.position}
          onChange={handleChange}
          className="form-control"
          min="0"
          required
        />
        <small>Les numéros plus petits apparaissent en premier</small>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <span>Actif</span>
        </label>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Annuler
        </button>
        <button type="submit" disabled={loading || uploadingImage} className="btn btn-primary">
          {loading ? 'Enregistrement...' : uploadingImage ? 'Téléchargement...' : slider ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
}

export default SliderForm;
